import { GeneralTypeInterface } from '../interfaces/Dto/GeneralTypeInterface'
import axios from 'axios'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import {
  ApplicationDjangoDto,
  AreaCodeDjangoDto,
  PhoneTypeDjangoDto,
} from '../interfaces/Django/CatalogDjangoDto'
import { apiDjango } from './ApiDjango'
import {
  CountryDto,
  CountryPhoneOption,
} from '../interfaces/General/CountryPhoneDto'
import { PHONE_COUNTRY_CODE, normalizeDialCode } from '../utils/shipmentValidation'

const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

export const GeneralTypePhoneServices = async (
  CountryId: string
): Promise<ListResponse<GeneralTypeInterface>> => {
  try {
    const response = await axios.get(
      `${REACT_APP_URL_PATH_API}/ListofType/listCodePhone?CountryId=${CountryId}`
    )
    if (response.status === 200) {
      var datamap = response.data
      return datamap
    }
  } catch (error) {}

  return [] as unknown as ListResponse<GeneralTypeInterface>
}

/** ISO alpha-3 de Venezuela: se fija primero en la lista de países. */
export const VENEZUELA_ISO = 'VEN'

/**
 * Países que se prefieren cuando VARIOS comparten el mismo prefijo y solo hay
 * un código guardado (la fila guarda "+1", no el ISO): un "+1" se muestra como
 * Estados Unidos, no como el primero que devuelva el catálogo.
 */
const DIAL_CODE_PRIORITY = ['USA', 'CAN']

/**
 * GET /Country (.NET) — catálogo de países con su código telefónico, para el
 * campo "País" del teléfono del destinatario en carga masiva.
 *
 * OJO: este endpoint devuelve un ARRAY pelado, no el envelope
 * `{ model, didError, ... }` del resto del API .NET. Se descartan los ~49
 * territorios sin código (no se puede armar un teléfono con ellos) y se
 * normaliza el prefijo a su primer token, porque el backend exige un solo
 * espacio entre código y número. NO lanza: devuelve `[]` y el llamador cae a la
 * lista mínima con Venezuela.
 */
export const getCountriesPhoneCodes = async (): Promise<CountryPhoneOption[]> => {
  try {
    const response = await axios.get(`${REACT_APP_URL_PATH_API}/Country`)
    if (response.status !== 200) return []
    return countryPhoneOptionsFromDto(
      Array.isArray(response.data) ? response.data : (response.data?.model ?? [])
    )
  } catch (error) {
    console.error('getCountriesPhoneCodes: fallo', { error })
    return []
  }
}

/** Un prefijo que el backend puede aceptar: `+` y 1 a 3 dígitos, nada más. */
const VALID_DIAL_CODE_RE = /^\+\d{1,3}$/

/**
 * Filas crudas del catálogo → opciones del combo. Separada del fetch para poder
 * probarla: el mapeo tiene más reglas que el llamado (códigos nulos, el área
 * pegada al prefijo, el orden).
 */
export const countryPhoneOptionsFromDto = (
  raw: CountryDto[]
): CountryPhoneOption[] => {
  const options = raw
    .filter((c) => (c?.countryPhoneAccessCode || '').trim() !== '')
    .map((c) => {
      const dialCode = normalizeDialCode(c.countryPhoneAccessCode)
      return {
        countryCodeIso: c.countryCodeIso,
        countryName: c.countryName,
        dialCode,
        label: `${c.countryName} (${dialCode})`,
      }
    })
    // El backend exige `^\+\d{1,3} …`. Una fila con el prefijo mal formado (el
    // campo ya llega sucio con el área pegada, "+1 809", así que asumir que ese
    // es el ÚNICO formato sucio es confiar de más en datos de terceros) daría
    // una opción que hace fallar toda fila que la elija, sin que el operador
    // tenga cómo arreglarlo. Mejor no ofrecerla.
    .filter((o) => {
      if (VALID_DIAL_CODE_RE.test(o.dialCode)) return true
      console.warn('getCountriesPhoneCodes: prefijo descartado', {
        countryCodeIso: o.countryCodeIso,
        dialCode: o.dialCode,
      })
      return false
    })

  // Venezuela primero (es el país del 99% de las guías); el resto alfabético.
  return options.sort((a, b) => {
    if (a.countryCodeIso === VENEZUELA_ISO) return -1
    if (b.countryCodeIso === VENEZUELA_ISO) return 1
    return a.countryName.localeCompare(b.countryName, 'es', {
      sensitivity: 'base',
    })
  })
}

/** Opción mínima cuando el catálogo no cargó: la fila sigue sirviendo para Venezuela. */
export const FALLBACK_COUNTRY_OPTIONS: CountryPhoneOption[] = [
  {
    countryCodeIso: VENEZUELA_ISO,
    countryName: 'Venezuela',
    dialCode: PHONE_COUNTRY_CODE,
    label: `Venezuela (${PHONE_COUNTRY_CODE})`,
  },
]

/**
 * Filtro del combo "País".
 *
 * `FormSearch` usa el TEXTO DEL INPUT como filtro (`onFilter(option, value)`),
 * así que el predicado tiene que aceptar lo que el propio combo escribe al
 * elegir una opción. Cuando el input decía "+58 República Bolivariana de
 * Venezuela" y el filtro solo miraba el nombre o el código, al reabrir el
 * desplegable no matcheaba nada y salía "Elementos no encontrados": el campo
 * no encontraba su propio valor. Ahora el input lleva solo el prefijo y este
 * filtro lo matchea explícitamente.
 *
 * Vive acá y no en cada componente porque la fila y la barra de acciones
 * masivas ofrecen el MISMO combo, y dos copias del predicado ya habían divergido.
 */
export const filterCountryOption = (
  option: CountryPhoneOption,
  filter: string
): boolean => {
  const q = (filter || '').toLowerCase().trim()
  if (q === '') return true
  // Por nombre ("colombia") y por la etiqueta completa ("colombia (+57)"), que
  // es lo que el operador ve en la lista y puede llegar a copiar.
  if (option.countryName.toLowerCase().includes(q)) return true
  if (option.label.toLowerCase().includes(q)) return true
  // Por prefijo, con o sin el `+`: el operador tipea "57" o "+57", y el propio
  // combo escribe "+57" al elegir.
  const digitosQuery = q.replace(/\D/g, '')
  if (!digitosQuery) return false
  return option.dialCode.replace('+', '').startsWith(digitosQuery)
}

/**
 * País que representa a un código guardado. Con varios candidatos (+1 es USA,
 * Canadá, República Dominicana…) gana el de `DIAL_CODE_PRIORITY` y, si ninguno
 * está, el primero del catálogo (que ya viene ordenado alfabéticamente).
 */
export const findCountryByDialCode = (
  options: CountryPhoneOption[],
  dialCode: string,
): CountryPhoneOption | undefined => {
  const matches = options.filter((o) => o.dialCode === dialCode)
  if (matches.length <= 1) return matches[0]
  for (const iso of DIAL_CODE_PRIORITY) {
    const hit = matches.find((o) => o.countryCodeIso === iso)
    if (hit) return hit
  }
  return matches[0]
}

/**
 * GET /api/phone/area-code (Django) — prefijos de operadora / codigos de area del pais.
 * La ruta se movio al controlador Phone (antes /api/area-code); el query param no cambio.
 * Reemplaza el .NET ListofType/listCodePhone. Drop-in: devuelve { value, text } para el
 * dropdown "Operadora".
 *
 * `area_code` llega CON cero inicial ("0212", "0412"); se quita porque todo el manejo de
 * telefono del front trabaja sin el (obtenerCodigoArea devuelve "412", tryParsePastedPhone
 * matchea contra el codigo sin 0, y la validacion exige operadora + 7 = 10 digitos).
 * `countryCode` es el codigo telefonico (58), NO el CountryId .NET (236). NO lanza.
 */
export const getAreaCodesDjango = async (
  countryCode?: string
): Promise<ListResponse<GeneralTypeInterface[]>> => {
  const errMsg = 'No se pudieron obtener los codigos de area'
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/phone/area-code`,
      { params: countryCode ? { country_code: countryCode } : undefined }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as AreaCodeDjangoDto[]
      return {
        model: list.map((dto) => {
          const raw = (dto.area_code ?? '').trim()
          const code = raw.startsWith('0') ? raw.slice(1) : raw
          return { value: code, text: code }
        }),
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const m = resp.data?.message || errMsg
    console.error('getAreaCodesDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m = (error as any)?.response?.data?.message || errMsg
    console.error('getAreaCodesDjango: fallo', { error })
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

/**
 * GET /api/phone-type (Django) — tipos de telefono del pais.
 * Drop-in de GeneralTypePhoneServices: mismo retorno { value, text } para alimentar los
 * dropdowns existentes. `value` = phone_type_code, `text` = phone_type_name.
 * Envelope Django: { code, error, message, data, error_data }. NO lanza.
 */
export const getPhoneTypesDjango = async (
  countryCode?: string
): Promise<ListResponse<GeneralTypeInterface[]>> => {
  const errMsg = 'No se pudieron obtener los tipos de telefono'
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/phone-type`,
      { params: countryCode ? { country_code: countryCode } : undefined }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as PhoneTypeDjangoDto[]
      return {
        model: list.map((dto) => ({
          value: dto.phone_type_code,
          text: dto.phone_type_name,
        })),
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const m = resp.data?.message || errMsg
    console.error('getPhoneTypesDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m = (error as any)?.response?.data?.message || errMsg
    console.error('getPhoneTypesDjango: fallo', { error })
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

/**
 * GET /api/application (Django) — busca aplicaciones.
 * OJO: los filtros son exact match (no parcial): name=Web NO encuentra "Web Portal";
 * hay que mandar el nombre completo. NO lanza.
 *
 * Los query params perdieron el prefijo `application_` y ya no se puede filtrar por id.
 * La RESPUESTA no cambio: sus campos siguen siendo application_id/_code/_name.
 */
export const getApplicationsDjango = async (params?: {
  code?: string
  name?: string
}): Promise<ListResponse<ApplicationDjangoDto[]>> => {
  const errMsg = 'No se pudieron obtener las aplicaciones'
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/application`,
      { params }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      return {
        model: (resp.data.data ?? []) as ApplicationDjangoDto[],
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const m = resp.data?.message || errMsg
    console.error('getApplicationsDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m = (error as any)?.response?.data?.message || errMsg
    console.error('getApplicationsDjango: fallo', { error })
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}
